import React, { useState } from 'react';
import {
  useGetHeldPayoutsQuery,
  useReleaseEventPayoutsMutation,
  type HeldPayoutSummary,
} from '../../redux/payouts/payouts.api';

/** Minor units → a readable amount in the payment's own currency. */
const money = (minor: number, currency: string): string =>
  `${(minor / 100).toFixed(2)} ${currency.toUpperCase()}`;

/**
 * What the transfer will actually carry.
 *
 * A ticket priced in USD settles onto a EUR balance, and it is the settled
 * figure that moves. Showing the charged pair here would promise the organizer
 * an amount they will not receive. Falls back to the charged pair for older
 * backends and for groups with no recorded settlement yet.
 */
const payout = (row: HeldPayoutSummary): { minor: number; currency: string } =>
  row.settledPayoutAmount != null && row.settlementCurrency != null
    ? { minor: row.settledPayoutAmount, currency: row.settlementCurrency }
    : { minor: row.payoutAmount, currency: row.currency };

/** True when the buyer was charged in a different currency than we pay out in. */
const isConverted = (row: HeldPayoutSummary): boolean =>
  payout(row).currency.toUpperCase() !== row.currency.toUpperCase();

const daysWaiting = (iso: string): number =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/**
 * Has the event actually happened?
 *
 * The hold covers exactly the window in which an event can still be cancelled.
 * Paying out before it is over means a later refund depends on reversing a
 * transfer, which can fail — the funds may not have settled, or the organizer
 * may already have moved them to their bank — and then the platform refunds the
 * buyer from its own balance.
 *
 * Multi-day events are only finished at their end. No dates at all counts as
 * finished rather than blocked forever. The backend enforces the same rule; this
 * is here so the operator sees why before clicking, not after.
 */
const hasFinished = (row: HeldPayoutSummary): boolean => {
  const ends = row.eventEndDate ?? row.eventStartDate;
  return !ends || new Date(ends).getTime() <= Date.now();
};

/** "in 3 d" / "in 5 h" — how long until the event is payable. */
const timeUntil = (iso: string): string => {
  const ms = new Date(iso).getTime() - Date.now();
  const hours = Math.ceil(ms / 3_600_000);
  return hours >= 24 ? `in ${Math.ceil(hours / 24)} d` : `in ${hours} h`;
};

export const AdminPayouts = (): React.JSX.Element => {
  const { data, isLoading, error, refetch } = useGetHeldPayoutsQuery();
  const [release, { isLoading: isReleasing }] = useReleaseEventPayoutsMutation();
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const onRelease = async (row: HeldPayoutSummary, force = false): Promise<void> => {
    if (force) {
      const ends = row.eventEndDate ?? row.eventStartDate;
      const early = window.confirm(
        `"${row.eventName}" has not finished yet${
          ends ? ` — it ends ${new Date(ends).toLocaleString()}` : ''
        }.\n\nPaying out now gives up the protection the hold exists for: if the ` +
          `event is cancelled, refunding the buyers depends on reversing this ` +
          `transfer, which can fail once the organizer has moved the money.\n\n` +
          `Pay out early anyway?`,
      );
      if (!early) return;
    }

    const ok = window.confirm(
      `Pay out ${money(payout(row).minor, payout(row).currency)} to ${
        row.organizerName || row.organizerEmail
      }?\n\nThis covers ${row.paymentsCount} ticket(s) for "${row.eventName}". ` +
        `The money leaves your Stripe balance and cannot be pulled back except ` +
        `by reversing the transfer.`,
    );
    if (!ok) return;

    setReleasingId(row.eventId);
    setResult(null);
    try {
      const res = await release({ eventId: row.eventId, force }).unwrap();
      setResult(
        res.failed.length === 0
          ? `Released ${res.released} payment(s) for "${row.eventName}".`
          : `Released ${res.released}, failed ${res.failed.length}: ` +
            res.failed.map((f) => `${f.paymentId} (${f.reason})`).join('; '),
      );
    } catch {
      setResult(`Payout failed for "${row.eventName}". Nothing was sent.`);
    } finally {
      setReleasingId(null);
    }
  };

  const rows = data ?? [];
  // Totals are per currency: events can be sold in several, and adding those
  // figures together would produce a number that means nothing.
  const totals = rows.reduce<Record<string, number>>((acc, r) => {
    const { minor, currency } = payout(r);
    acc[currency.toUpperCase()] = (acc[currency.toUpperCase()] ?? 0) + minor;
    return acc;
  }, {});
  const totalsLabel = Object.entries(totals)
    .map(([currency, minor]) => money(minor, currency))
    .join(' + ');

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Payouts</h2>
        </div>
        <button className="admin-users__page-btn" onClick={() => refetch()}>
          Refresh
        </button>
      </div>

      <p className="app-settings__hint">
        Money from ticket sales sits on the platform balance until you release
        it. Nothing is forwarded automatically — that hold is what stops a
        fraudulent buyer&apos;s money reaching an organizer before the
        chargeback arrives. Check the event actually happened, then pay out.
        <br />
        <strong>Disputed payments never appear here</strong> and cannot be paid
        out: the network has already taken that money back.
      </p>

      {isLoading && <div className="admin-users__loading">Loading payouts…</div>}
      {error != null && <div className="admin-users__error">Could not load payouts.</div>}
      {result != null && <p className="app-settings__hint">{result}</p>}

      {!isLoading && rows.length === 0 && (
        <p className="app-settings__hint">Nothing is waiting to be paid out.</p>
      )}

      {rows.length > 0 && (
        <>
          <p className="app-settings__hint">
            <strong>{rows.length}</strong> event(s) waiting,{' '}
            <strong>{totalsLabel}</strong> held in total.
          </p>

          <div className="admin-users__table-wrap">
            <table className="admin-users__table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Organizer</th>
                <th>Tickets</th>
                <th>Gross</th>
                <th>To organizer</th>
                <th>Waiting</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.eventId}>
                  <td>{row.eventName}</td>
                  <td>
                    {row.organizerName || row.organizerEmail}
                    {row.connectedAccountId == null && (
                      <>
                        {' '}
                        <span className="admin-error">
                          (no connected account)
                        </span>
                      </>
                    )}
                  </td>
                  <td>{row.paymentsCount}</td>
                  <td>{money(row.grossAmount, row.currency)}</td>
                  <td>
                    <strong>{money(payout(row).minor, payout(row).currency)}</strong>
                    {isConverted(row) && (
                      <>
                        <br />
                        <span className="app-settings__hint">
                          sold as {money(row.payoutAmount, row.currency)}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    {daysWaiting(row.oldestPaymentAt)} d
                    {!hasFinished(row) && (
                      <>
                        <br />
                        <span className="app-settings__hint">
                          event ends{' '}
                          {timeUntil(
                            (row.eventEndDate ?? row.eventStartDate) as string,
                          )}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <button
                      className="admin-users__page-btn"
                      disabled={
                        isReleasing ||
                        releasingId === row.eventId ||
                        row.connectedAccountId == null
                      }
                      onClick={() => onRelease(row, !hasFinished(row))}
                      title={
                        hasFinished(row)
                          ? undefined
                          : 'This event has not finished. Paying out now gives up the protection the hold exists for.'
                      }
                    >
                      {releasingId === row.eventId
                        ? 'Paying…'
                        : hasFinished(row)
                          ? 'Pay out'
                          : 'Pay out early'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
};
